import moment from 'moment'
import 'moment-timezone'
import DatePicker from 'react-datepicker'
import 'react-datepicker/dist/react-datepicker.css'
import {
  CustomCalendarContainerPropsType,
  DateInputPropsType,
} from '../../utils/types'
import SvgCalendarIcon from '../../icons/common/svgCalendar'
import { useEffect, useMemo, useRef, useState } from 'react'
import { DATE_INPUT_CONSTANTS, FIELD_VALUE_CONSTANTS } from '../../utils'
import { uniqueId } from 'lodash'

const DateInput = ({
  isDisabled,
  value,
  handleChange,
  className,
  showClear,
  showIcon = true,
  tabIndex,
  name,
  min,
  max,
  placeholder,
  includeTime,
}: DateInputPropsType) => {
  const ref = useRef<DatePicker | null>(null)
  const minDateObj = min
    ? new Date(min)
    : new Date(DATE_INPUT_CONSTANTS.MIN_DATE)
  const maxDateObj = max
    ? new Date(max)
    : new Date(DATE_INPUT_CONSTANTS.MAX_DATE)
  const [top, setTop] = useState<number>(0)
  const [isOpen, setIsOpen] = useState(false)
  const uniqueString = useMemo(() => {
    return uniqueId(name ?? '')
  }, [name])
  const handeOnChange = (
    date: Date | null
    // event?:
    //   | React.MouseEvent<HTMLElement, MouseEvent>
    //   | React.KeyboardEvent<HTMLElement>
    //   | undefined
  ) => {
    if (handleChange) {
      if (date) {
        if (includeTime)
          handleChange(
            moment(date).format(DATE_INPUT_CONSTANTS.COMMON_DATE_TIME_FORMAT_2)
          )
        else
          handleChange(
            moment(date).format(DATE_INPUT_CONSTANTS.DATE_FORMAT_API)
          )
      } else handleChange('')
    }
    setIsOpen(false)
  }

  const getSelectedValueFormat = () => {
    return includeTime
      ? DATE_INPUT_CONSTANTS.COMMON_DATE_TIME_FORMAT_2
      : DATE_INPUT_CONSTANTS.COMMON_DATE_FORMAT
  }
  const getMinTime = () => {
    if (!value) return undefined
    if (moment(value).isSame(minDateObj, 'day')) {
      return minDateObj
    }
    return moment().startOf('day').toDate()
  }

  const getMaxTime = () => {
    if (!value) return undefined
    if (moment(value).isSame(maxDateObj, 'day')) {
      return maxDateObj
    }
    return moment().endOf('day').toDate()
  }

  useEffect(() => {
    if (ref?.current) {
      const rect =
        ref?.current?.input?.parentElement?.parentElement?.parentElement?.getBoundingClientRect()
      if (rect?.y) {
        setTop(rect.y + rect.height)
      }
    }
  }, [])

  return (
    <DatePicker
      ref={ref}
      name={name}
      selected={
        value && moment(value, getSelectedValueFormat(), true).isValid()
          ? moment(value, getSelectedValueFormat(), true).toDate()
          : null
      }
      open={isOpen}
      onClickOutside={(event) => {
        const target = event.target as HTMLElement

        if (target.closest(`.custom-datepicker-icon-${uniqueString}`)) {
          // Don't close the calendar if the click is within the icon
          return
        }

        setIsOpen(false)
      }}
      onSelect={() => {
        setIsOpen(false)
      }}
      onInputClick={() => {
        setIsOpen((prev) => !prev)
      }}
      className={`${className} border border-border-200 px-1 rounded p-0`}
      onChange={handeOnChange}
      disabled={isDisabled}
      dateFormat={
        includeTime
          ? DATE_INPUT_CONSTANTS.COMMON_DATE_FORMAT_FOR_DATE_TIME_INPUT
          : DATE_INPUT_CONSTANTS.COMMON_DATE_FORMAT_FOR_DATE_INPUT
      }
      placeholderText={placeholder}
      icon={
        <div
          className={`flex justify-center bg-white opacity-1  !w-[.75rem] !p-0 custom-datepicker-icon-${uniqueString} ${
            isDisabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'
          }`}
          onClick={(e) => {
            e?.stopPropagation()
            e?.preventDefault()
            if (!isDisabled) {
              setIsOpen((prev) => !prev)
            }
          }}
        >
          <SvgCalendarIcon
            className={`${
              isDisabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'
            }`}
          />
        </div>
      }
      showIcon={showIcon}
      wrapperClassName={`date-input-wrapper w-full`}
      calendarContainer={(props) => (
        <CustomCalendarContainer
          {...props}
          onClear={() => {
            handeOnChange(null)
            setIsOpen(false)
          }}
          onTodayClick={() => {
            handeOnChange(moment().toDate())
            setIsOpen(false)
          }}
          showClear={showClear}
        />
      )}
      showYearDropdown
      dropdownMode="scroll"
      yearDropdownItemNumber={DATE_INPUT_CONSTANTS.DEFAULT_YEARS_TO_SHOW}
      popperClassName={`z-[9999] bg-white shadow-md rounded-lg !t-[${top}px]`}
      tabIndex={tabIndex}
      minDate={minDateObj}
      maxDate={maxDateObj}
      {...(includeTime
        ? {
            showTimeSelect: true,
            timeFormat: 'HH:mm',
            timeIntervals: FIELD_VALUE_CONSTANTS.FIFTEEN,
            timeCaption: 'Time',
            minTime: getMinTime(),
            maxTime: getMaxTime(),
          }
        : {})}
    />
  )
}

const CustomCalendarContainer = ({
  className,
  children,
  onClear,
  onTodayClick,
  showClear = true,
}: CustomCalendarContainerPropsType) => {
  return (
    <div
      style={{ zIndex: 9999, position: 'relative' }}
      className={
        className +
        ' customwrapper flex flex-col z-[9999] bg-white shadow-md rounded-lg'
      }
    >
      <div>{children}</div>
      <div className="flex justify-around gap-2 mt-2 border-t p-2 ">
        <button
          type="button"
          style={{ zIndex: 9999 }}
          className="text-xs text-blue-600"
          onClick={() => {
            onTodayClick()
          }}
        >
          {DATE_INPUT_CONSTANTS.TODAY}
        </button>
        {showClear && (
          <button
            style={{ zIndex: 9999 }}
            className="text-xs text-red-600"
            type="button"
            onClick={() => {
              onClear()
            }}
          >
            {DATE_INPUT_CONSTANTS.CLEAR}
          </button>
        )}
      </div>
    </div>
  )
}
export default DateInput

export const DateTimeInputComponent = ({
  isDisabled,
  value,
  handleChange,
  className,
  showClear,
  showIcon = true,
  tabIndex,
  name,
  min,
  max,
  placeholder,
}: DateInputPropsType) => {
  const browserTimeZone = moment.tz.guess()
  const ref = useRef<DatePicker | null>(null)
  const [top, setTop] = useState<number>(0)
  const [isOpen, setIsOpen] = useState(false)
  const uniqueString = useMemo(() => {
    return uniqueId(name ?? '')
  }, [name])
  // Convert incoming string to Date in local timezone
  const momentInTz = value ? moment.tz(value, browserTimeZone) : null
  const dateInTz = momentInTz?.toDate() ?? null

  // When user selects a new date
  const handleOnChange = (date: Date | null) => {
    if (!handleChange) return

    if (!date) {
      handleChange('')
    } else {
      // Convert from local picked date (assumed in browser timezone) to UTC ISO string
      const iso = moment.tz(date, browserTimeZone).utc().toISOString()
      handleChange(iso)
    }
  }
  useEffect(() => {
    if (ref?.current) {
      const rect =
        ref?.current?.input?.parentElement?.parentElement?.parentElement?.getBoundingClientRect()
      if (rect?.y) {
        setTop(rect.y + rect.height)
      }
    }
  }, [])
  return (
    <DatePicker
      ref={ref}
      name={name}
      selected={dateInTz}
      open={isOpen}
      onClickOutside={(event) => {
        const target = event.target as HTMLElement

        if (target.closest(`.custom-datepicker-icon-${uniqueString}`)) {
          // Don't close the calendar if the click is within the icon
          return
        }

        setIsOpen(false)
      }}
      onSelect={() => {
        setIsOpen(false)
      }}
      onInputClick={() => {
        setIsOpen((prev) => !prev)
      }}
      className={`${className} border border-border-200 px-1 rounded p-0`}
      onChange={handleOnChange}
      showTimeSelect
      timeFormat="HH:mm"
      timeIntervals={FIELD_VALUE_CONSTANTS.FIFTEEN}
      timeCaption="Time"
      disabled={isDisabled}
      dateFormat={
        DATE_INPUT_CONSTANTS.SUPPOERTED_DATE_TIME_FORMAT_DATE_TIME_INPUT_FIELD
      }
      value={
        dateInTz
          ? moment(dateInTz)
              .tz(browserTimeZone)
              .format(DATE_INPUT_CONSTANTS.COMMON_DATE_TIME_FORMAT)
          : ''
      }
      placeholderText={placeholder}
      icon={
        <div
          className={`flex justify-center bg-white opacity-1 cursor-pointer !w-[.75rem] !p-0 custom-datepicker-icon-${uniqueString} ${
            isDisabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'
          }`}
          onClick={(e) => {
            e?.stopPropagation()
            e?.preventDefault()
            if (!isDisabled) {
              setIsOpen((prev) => !prev)
            }
          }}
        >
          <SvgCalendarIcon
            className={`${
              isDisabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'
            }`}
          />
        </div>
      }
      showIcon={showIcon}
      wrapperClassName={`date-input-wrapper w-full`}
      calendarContainer={(props) => (
        <CustomCalendarContainer
          {...props}
          onClear={() => {
            handleOnChange(null)
            setIsOpen(false)
          }}
          onTodayClick={() => {
            handleOnChange(moment().toDate())
            setIsOpen(false)
          }}
          showClear={showClear}
        />
      )}
      showYearDropdown
      dropdownMode="scroll"
      yearDropdownItemNumber={DATE_INPUT_CONSTANTS.DEFAULT_YEARS_TO_SHOW}
      popperClassName={`z-[9999] bg-white shadow-md rounded-lg !t-[${top}px]`}
      tabIndex={tabIndex}
      minDate={min ? new Date(min) : new Date(DATE_INPUT_CONSTANTS.MIN_DATE)}
      maxDate={max ? new Date(max) : new Date(DATE_INPUT_CONSTANTS.MAX_DATE)}
    />
  )
}
